import { useEffect, useMemo, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useGetMyChildrenQuery } from '../store/api/localApi';
import { parentLandingPath, readRememberedChild, resolveLandingChild, writeRememberedChild } from './rememberedChild';

export type ActiveStudent = ReturnType<typeof toActiveStudent> | null;

function toActiveStudent(child: {
  id: string;
  firstName: string;
  lastName: string;
  displayName: string;
  locationId: string;
  locationName: string;
  organizationId: string;
  organizationName: string;
  registrationStatus: string;
  // Task 17: LEFT children stay reachable (read-only) so parents keep access to
  // their history. Consumers gate write actions on `status === 'LEFT'`.
  status: 'PENDING_START' | 'ACTIVE' | 'LEFT';
  grade?: string;
  badges: { pendingForms: number; registrationPending: boolean };
}) {
  return child;
}

/**
 * Active-student resolver for parent routes.
 *
 * The URL is the truth: `/parent/<studentId>/...`. Family pages live under
 * `/parent/family/...` and name no child. We re-resolve on every render
 * against the children list. If the URL studentId is unknown (deleted? typo?
 * shared by another parent?), the caller decides — most parent routes
 * redirect to `parentHomePath`.
 *
 * The last child opened is remembered in localStorage (`rememberedChild.ts`)
 * so a launch reopens that child instead of asking again.
 *
 * Returns:
 *   - activeStudent      — the child named in the URL, or null
 *   - children           — full list (for the picker)
 *   - isLoading          — query in flight
 *   - parentHomePath     — '/parent/<id>' for one child or the remembered
 *                          child, '/parent/choose' when a multi-child parent
 *                          has nothing (valid) remembered, '/parent/onboarding'
 *                          when zero. Used for login redirect and "back to
 *                          home" links.
 *   - homeChildId        — the child the tab bar and family-page nav link to:
 *                          the active child, else the landing child; null only
 *                          for zero children or an undecided multi-child parent.
 *   - isFamilyPage       — true on /parent/family/*
 *   - isChooserPage      — true on /parent/choose (the child picker page)
 *   - goToStudent(id)    — programmatic switch (preserves current sub-path
 *                          where it makes sense, otherwise lands on the
 *                          student's home).
 */
export function useActiveStudent() {
  const params = useParams<{ studentId?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { data, isLoading } = useGetMyChildrenQuery();

  const children = data?.children ?? [];
  // useParams resolves against the nearest matching <Route>. When this
  // hook runs above the parent route subtree (e.g. inside Layout), there
  // is no :studentId binding — fall back to parsing the pathname so the
  // picker still recognises /parent/<id>.
  const studentIdFromUrl = params.studentId ?? extractStudentIdFromPath(location.pathname);

  const activeStudent = useMemo(() => {
    if (!studentIdFromUrl) return null;
    const found = children.find((c) => c.id === studentIdFromUrl);
    return found ? toActiveStudent(found) : null;
  }, [studentIdFromUrl, children]);

  const [remembered, setRemembered] = useState<string | null>(() => readRememberedChild());
  useEffect(() => {
    if (!activeStudent) return;
    writeRememberedChild(activeStudent.id);
    setRemembered(activeStudent.id);
  }, [activeStudent?.id]);

  // Preferred landing for this parent. Stable so callers can use it
  // as a Navigate `to`.
  const parentHomePath = isLoading ? null : parentLandingPath(children, remembered);
  const homeChildId = activeStudent?.id ?? resolveLandingChild(children, remembered);
  const isFamilyPage = location.pathname.startsWith('/parent/family/');
  const isChooserPage = location.pathname.startsWith('/parent/choose');

  // Switch the active student while keeping the sub-path where reasonable.
  // E.g. clicking another kid from /parent/<id>/forms keeps you in /forms.
  // Some sub-paths only make sense for the current student (e.g. a form
  // fill keyed to a specific invitation); the caller can opt out by
  // navigating with `subPath: ''`.
  const goToStudent = (id: string, subPath?: string) => {
    const trailing = subPath ?? extractTrailingSubPath(location.pathname);
    navigate(`/parent/${id}${trailing}`);
  };

  return {
    activeStudent,
    children,
    isLoading,
    parentHomePath,
    homeChildId,
    isFamilyPage,
    isChooserPage,
    goToStudent,
  };
}

/**
 * Pull the studentId directly out of /parent/<id>/...
 * Returns undefined when the path isn't under /parent. Used as a fallback
 * for places that read this hook above the route boundary where useParams
 * can't see the dynamic segment.
 */
// Reserved first segments under /parent/ that are NOT a studentId: account
// pages, onboarding, family pages, the chooser, and the retired `all` (kept
// only as redirects). Reading one as a child makes the parent frame think a
// specific child is active (e.g. it surfaced the child-scoped "Incident
// reports" nav item on /parent/account/pickup-contacts).
const RESERVED_PARENT_SEGMENTS = new Set(['account', 'onboarding', 'all', 'family', 'choose']);

export function extractStudentIdFromPath(pathname: string): string | undefined {
  const match = pathname.match(/^\/parent\/([^/]+)/);
  if (!match) return undefined;
  const segment = match[1];
  return RESERVED_PARENT_SEGMENTS.has(segment) ? undefined : segment;
}

/**
 * Strip the leading /parent/<id> from a pathname, returning the
 * remainder (which may include a leading slash). Returns '' when there's
 * no remainder.
 */
export function extractTrailingSubPath(pathname: string): string {
  // Account, family and other reserved pages name no child, so there is no
  // child sub-path to carry across (e.g. /parent/account/pickup-contacts).
  if (!extractStudentIdFromPath(pathname)) return '';
  const match = pathname.match(/^\/parent\/[^/]+(\/.*)?$/);
  if (!match) return '';
  // Some sub-paths are tied to the previous student (e.g. a form fill
  // invitation id) and shouldn't be carried across. We allow-list the
  // ones that are kid-agnostic at the *route shape* level — concrete
  // resource ids in the path are dropped.
  const tail = match[1] ?? '';
  if (!tail) return '';
  // If the tail names a specific resource (e.g. /forms/<uuid>/fill or
  // /absence/<uuid>), drop the resource id — we want to land on the
  // collection view for the new student, not stale resource ids.
  const segments = tail.split('/').filter(Boolean);
  if (segments[0] === 'forms') return '/forms';
  if (segments[0] === 'absence') return '/absence';
  if (segments[0] === 'profile') return '/profile';
  if (segments[0] === 'threads') return '/threads';
  if (segments[0] === 'incidents') return '/incidents';
  if (segments[0] === 'moments') return '/moments';
  // One observation belongs to one child; the new child lands on their home.
  if (segments[0] === 'observations') return '';
  return tail;
}
